import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { t, i18n } = useTranslation();
  const { slug } = useParams<{ slug: string }>();
  const [scrolled, setScrolled] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const article = articles.find((a) => a.slug === slug) || articles[0];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
    setLangOpen(false);
    setMenuOpen(false);
  };

  const languages = [
    { code: 'en', label: 'English' },
    { code: 'fr', label: 'Français' },
    { code: 'de', label: 'Deutsch' },
    { code: 'da', label: 'Dansk' },
  ];

  return (
    <div className="min-h-screen bg-warm-50">
      {/* Navbar — always visible white bar */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-sage-100">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="flex items-center justify-between h-16 md:h-20">
            <Link to="/" className="flex items-center gap-2">
              <span className="font-heading font-bold text-lg md:text-xl tracking-tight text-sage-800">
                AllChinaBuy<span className="font-light">Spreadsheet</span>
              </span>
            </Link>

            <div className="flex items-center gap-2 md:gap-6">
              <div className="hidden md:flex items-center gap-6">
                <Link to="/" className="text-sm font-medium text-sage-800 hover:text-coral-500 transition whitespace-nowrap">
                  {t('navHome')}
                </Link>
                <Link to="/article/allchinabuy-buying-guide-beginners" className="text-sm font-medium text-sage-800 hover:text-coral-500 transition whitespace-nowrap">
                  {t('navShoppingGuide')}
                </Link>
                <Link to="/" className="text-sm font-medium text-sage-800 hover:text-coral-500 transition whitespace-nowrap">
                  {t('navGuides')}
                </Link>
              </div>

              <div className="relative">
                <button
                  onClick={() => setLangOpen(!langOpen)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition bg-sage-100 text-sage-800 hover:bg-sage-200"
                >
                  <i className="ri-global-line w-4 h-4 flex items-center justify-center" />
                  <span className="hidden sm:inline">{languages.find(l => l.code === i18n.language)?.label || 'English'}</span>
                  <i className={`ri-arrow-down-s-line w-4 h-4 flex items-center justify-center transition-transform ${langOpen ? 'rotate-180' : ''}`} />
                </button>
                {langOpen && (
                  <div className="absolute right-0 mt-2 w-40 bg-white rounded-xl shadow-lg border border-sage-100 overflow-hidden z-50">
                    {languages.map(lang => (
                      <button
                        key={lang.code}
                        onClick={() => changeLanguage(lang.code)}
                        className={`w-full text-left px-4 py-2.5 text-sm hover:bg-sage-50 transition ${i18n.language === lang.code ? 'text-sage-700 font-medium bg-sage-50' : 'text-sage-800'}`}
                      >
                        {lang.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Mobile hamburger */}
              <button
                onClick={() => setMenuOpen(!menuOpen)}
                className="md:hidden w-10 h-10 rounded-lg bg-sage-100 flex items-center justify-center text-sage-800"
                aria-label="Menu"
              >
                <i className={`${menuOpen ? 'ri-close-line' : 'ri-menu-line'} w-5 h-5 flex items-center justify-center`} />
              </button>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        {menuOpen && (
          <div className="md:hidden bg-white border-t border-sage-100 max-h-[70vh] overflow-y-auto">
            <div className="px-4 py-4 space-y-1">
              <Link to="/" onClick={() => setMenuOpen(false)} className="flex items-center gap-3 px-3 py-3 rounded-lg text-sage-800 font-medium hover:bg-sage-50 transition">
                <i className="ri-home-4-line w-5 h-5 flex items-center justify-center text-coral-500" />
                {t('navHome')}
              </Link>
              <Link to="/article/allchinabuy-buying-guide-beginners" onClick={() => setMenuOpen(false)} className="flex items-center gap-3 px-3 py-3 rounded-lg text-sage-800 font-medium hover:bg-sage-50 transition">
                <i className="ri-shopping-bag-3-line w-5 h-5 flex items-center justify-center text-coral-500" />
                {t('navShoppingGuide')}
              </Link>
              <Link to="/" onClick={() => setMenuOpen(false)} className="flex items-center gap-3 px-3 py-3 rounded-lg text-sage-800 font-medium hover:bg-sage-50 transition">
                <i className="ri-article-line w-5 h-5 flex items-center justify-center text-coral-500" />
                {t('navGuides')}
              </Link>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Header */}
      <div className="relative h-[45vh] md:h-[55vh] flex items-end overflow-hidden mt-16 md:mt-20">
        <div className="absolute inset-0">
          <img src={article.image} alt={article.title} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/40 to-black/30" />
        </div>
        <div className="relative z-10 w-full px-4 md:px-8 lg:px-12 pb-10 md:pb-14 max-w-5xl mx-auto">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/15 backdrop-blur-md border border-white/20 text-white/90 text-xs mb-4">
            <i className="ri-article-line w-3.5 h-3.5 flex items-center justify-center" />
            {article.tag} · {article.readTime}
          </div>
          <h1 className="font-heading text-2xl md:text-4xl lg:text-5xl font-bold text-white leading-tight mb-4 max-w-4xl text-balance">
            {article.title}
          </h1>
          <p className="text-white/80 text-sm md:text-base max-w-2xl">{article.description}</p>
        </div>
      </div>

      <div className="w-full px-4 md:px-8 lg:px-12 max-w-5xl mx-auto py-12 md:py-16">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm text-sage-500 mb-8">
          <Link to="/" className="hover:text-coral-500 transition">{t('navHome')}</Link>
          <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center" />
          <span className="text-sage-800 font-medium">{article.tag}</span>
        </nav>

        <article className="max-w-none">
          <p className="text-lg md:text-xl text-sage-700 leading-relaxed mb-10">{article.intro}</p>

          {article.sections.map((section, idx) => (
            <section key={idx} className="mb-10">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-sage-900 mb-4">{section.heading}</h2>
              {section.paragraphs.map((para, pIdx) => (
                <p key={pIdx} className="text-sage-700 leading-relaxed mb-4">{para}</p>
              ))}
            </section>
          ))}

          {/* FAQ */}
          <section className="mb-10">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-sage-900 mb-5">Frequently Asked Questions</h2>
            <div className="space-y-4">
              {article.faqs.map((faq, idx) => (
                <div key={idx} className="bg-white rounded-xl border border-sage-100 overflow-hidden">
                  <div className="px-5 py-4 bg-sage-50 border-b border-sage-100">
                    <h3 className="font-semibold text-sage-900 text-sm">{faq.q}</h3>
                  </div>
                  <div className="p-5">
                    <p className="text-sm text-sage-700 leading-relaxed">{faq.a}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* CTA */}
          <div className="bg-sage-800 rounded-2xl p-8 md:p-10 text-center mt-10">
            <h3 className="font-heading text-xl md:text-2xl font-bold text-white mb-4">Ready to Shop Smarter?</h3>
            <p className="text-white/70 mb-6 max-w-xl mx-auto">Explore the full AllChinaBuy spreadsheet with curated product links, trusted sellers, and updated prices across every category.</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-coral-500 text-white font-semibold text-sm hover:bg-coral-600 transition whitespace-nowrap"
              >
                <i className="ri-external-link-line w-4 h-4 flex items-center justify-center" />
                {t('ctaExplore')}
              </a>
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-white/10 text-white border border-white/20 font-semibold text-sm hover:bg-white/20 transition whitespace-nowrap"
              >
                <i className="ri-home-4-line w-4 h-4 flex items-center justify-center" />
                {t('backToHome')}
              </Link>
            </div>
          </div>

          {/* Internal Links */}
          <div className="border-t border-sage-100 pt-8 mt-8">
            <h3 className="font-heading text-lg font-bold text-sage-900 mb-4">{t('relatedArticles')}</h3>
            <div className="grid sm:grid-cols-2 gap-3">
              {articles.filter(a => a.slug !== article.slug).slice(0, 4).map(a => (
                <Link key={a.slug} to={`/article/${a.slug}`} className="flex items-center gap-3 p-4 rounded-xl bg-white border border-sage-100 hover:border-coral-200 hover:bg-coral-50 transition group">
                  <i className="ri-article-line text-coral-500 w-5 h-5 flex items-center justify-center flex-shrink-0" />
                  <span className="text-sm font-medium text-sage-800 group-hover:text-coral-700 leading-snug">{a.title}</span>
                </Link>
              ))}
              <Link to="/" className="flex items-center gap-3 p-4 rounded-xl bg-white border border-sage-100 hover:border-coral-200 hover:bg-coral-50 transition group">
                <i className="ri-home-4-line text-coral-500 w-5 h-5 flex items-center justify-center flex-shrink-0" />
                <span className="text-sm font-medium text-sage-800 group-hover:text-coral-700">AllChinaBuy Spreadsheet Homepage</span>
              </Link>
            </div>
          </div>
        </article>
      </div>

      {/* Footer */}
      <footer className="bg-sage-900 text-white/80 py-12 md:py-16">
        <div className="w-full px-4 md:px-8 lg:px-12 max-w-7xl mx-auto">
          <div className="mb-8">
            <h3 className="font-heading text-lg font-bold text-white mb-2">
              AllChinaBuy<span className="font-light">Spreadsheet</span>
            </h3>
            <p className="text-sm text-white/60 leading-relaxed max-w-2xl">
              {t('footerDisclaimer')}
            </p>
          </div>
          <div className="border-t border-white/10 pt-6 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-xs text-white/40">{t('footerCopyright')}</p>
            <div className="flex items-center gap-2">
              {languages.map(lang => (
                <button
                  key={lang.code}
                  onClick={() => changeLanguage(lang.code)}
                  className={`text-xs px-2 py-1 rounded transition ${i18n.language === lang.code ? 'bg-white/20 text-white' : 'text-white/40 hover:text-white'}`}
                >
                  {lang.code.toUpperCase()}
                </button>
              ))}
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}